import {
  Avatar,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  List,
  ListItem,
  ListItemAvatar,
  ListItemButton,
  ListItemText,
  TextField,
  Typography,
} from "@mui/material";
import { useCallback, useEffect, useState } from "react";
import type { Product } from "../../product/product.entity";
import { productRepository } from "../../product/product.repository";

type SellProductPickerDialogProps = {
  open: boolean;
  initialTerm: string;
  onClose: () => void;
  onPick: (product: Product) => void;
  onRegisterNew: (searchTerm: string) => void;
};

export function SellProductPickerDialog({
  open,
  initialTerm,
  onClose,
  onPick,
  onRegisterNew,
}: SellProductPickerDialogProps) {
  const [term, setTerm] = useState(initialTerm);
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState<Product[]>([]);

  const runSearch = useCallback(async (q: string) => {
    const t = q.trim();
    if (!t) {
      setResults([]);
      return;
    }
    setLoading(true);
    try {
      const list = await productRepository.searchProductsForSell(t, 40);
      setResults(list);
    } catch {
      setResults([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!open) return;
    setTerm(initialTerm);
  }, [open, initialTerm]);

  useEffect(() => {
    if (!open) return;
    const h = window.setTimeout(() => {
      void runSearch(term);
    }, 280);
    return () => window.clearTimeout(h);
  }, [open, term, runSearch]);

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>Escolher produto</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
          Digite código, nome ou trecho da descrição. Toque em um item para adicionar à venda.
        </Typography>
        <TextField
          autoFocus
          fullWidth
          label="Busca"
          value={term}
          onChange={(e) => setTerm(e.target.value)}
          placeholder="Ex.: 789123, arroz 5kg, atum…"
        />
        {loading ? (
          <Typography variant="body2" sx={{ mt: 2 }}>
            Buscando…
          </Typography>
        ) : null}
        <List dense sx={{ maxHeight: "50vh", overflow: "auto", mt: 1 }}>
          {results.length === 0 && !loading && term.trim().length > 0 ? (
            <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
              Nenhum resultado. Cadastre um produto novo ou ajuste o termo.
            </Typography>
          ) : null}
          {results.map((p) => (
            <ListItem key={p.id} disablePadding>
              <ListItemButton
                onClick={() => {
                  onPick(p);
                  onClose();
                }}
              >
                <ListItemAvatar>
                  <Avatar variant="rounded" src={p.getImage()} alt="" />
                </ListItemAvatar>
                <ListItemText
                  primary={p.getName() || "Sem nome"}
                  secondary={
                    <Box component="span" sx={{ display: "block" }}>
                      <Typography component="span" variant="body2" color="text.primary">
                        {p
                          .getPrice()
                          .toLocaleString("pt-BR", { style: "currency", currency: "BRL" })}
                      </Typography>
                      <Typography component="span" variant="caption" color="text.secondary" display="block">
                        {p.getCodeLabelForUi()}
                      </Typography>
                    </Box>
                  }
                />
              </ListItemButton>
            </ListItem>
          ))}
        </List>
      </DialogContent>
      <DialogActions sx={{ flexWrap: "wrap", gap: 1, px: 3, pb: 2 }}>
        <Button onClick={() => onRegisterNew(term)} color="primary" variant="contained">
          Cadastrar novo
        </Button>
        <Button onClick={onClose}>Fechar</Button>
      </DialogActions>
    </Dialog>
  );
}
